import React, { useMemo } from 'react';
import { graphql, useFragment } from 'react-relay';
import { LinkButton } from '@atlaskit/button/new';
import EmptyState from '@atlaskit/empty-state';
import { fg } from '@atlassian/jira-feature-gating';
import { useIntl } from '@atlassian/jira-intl';
import type { noPermissions_projectsDirectoryV3$key } from '@atlassian/jira-relay/src/__generated__/noPermissions_projectsDirectoryV3.graphql';
import { useTenantContext } from '@atlassian/jira-tenant-context-controller/src/components/tenant-context/index.tsx';
import imageUrl from './assets/empty.svg';
import messages from './messages.tsx';
import { SignInRedirect } from './sign-in-redirect/index.tsx';

export interface Props {
	dataRef: noPermissions_projectsDirectoryV3$key;
}

export interface PrimaryActionButtonProps {
	canManagePermissions: boolean;
	canContactAdmins: boolean;
}

const PrimaryActionButton = ({
	canManagePermissions,
	canContactAdmins,
}: PrimaryActionButtonProps) => {
	const { formatMessage } = useIntl();

	if (canManagePermissions) {
		return (
			<LinkButton appearance="primary" href="/secure/admin/ViewPermissionSchemes.jspa">
				{formatMessage(messages.buttonAdmin)}
			</LinkButton>
		);
	}

	if (canContactAdmins) {
		return (
			<LinkButton appearance="primary" href="/secure/ContactAdministrators!default.jspa">
				{formatMessage(messages.buttonUser)}
			</LinkButton>
		);
	}
	return null;
};

export const NoPermissions = ({ dataRef }: Props) => {
	const { formatMessage } = useIntl();
	const tenantContext = useTenantContext();

	const { jira } = useFragment<noPermissions_projectsDirectoryV3$key>(
		graphql`
			fragment noPermissions_projectsDirectoryV3 on Query {
				jira {
					canContactAdmin: applicationPropertiesByKey(
						cloudId: $cloudId
						keys: "jira.show.contact.administrators.form"
					) {
						key
						value
					}
				}
			}
		`,
		dataRef,
	);

	const canManagePermissions = tenantContext?.isAdmin;

	const canContactAdmins = useMemo(
		() =>
			jira?.canContactAdmin?.find(({ key }) => key === 'jira.show.contact.administrators.form')
				?.value === 'true',
		[jira?.canContactAdmin],
	);

	const newDesc = canManagePermissions ? (
		<>{formatMessage(messages.instructionsAdmin)}</>
	) : (
		<SignInRedirect />
	);

	return (
		<EmptyState
			width="narrow"
			imageUrl={imageUrl}
			header={formatMessage(
				fg('jira-issue-terminology-refresh-m3') ? messages.headerIssueTermRefresh : messages.header,
			)}
			description={newDesc}
			primaryAction={
				<PrimaryActionButton
					canManagePermissions={canManagePermissions}
					canContactAdmins={canContactAdmins}
				/>
			}
		/>
	);
};
